import React, { useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { setError } from "../reducers/appSlice";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Link from '@mui/material/Link';
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiFormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableFooter from "@mui/material/TableFooter";

import SearchIcon from "@mui/icons-material/Search";
import CachedIcon from "@mui/icons-material/Cached";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import DoneOutlineRounded from "@mui/icons-material/DoneOutlineRounded";

import Modal from "../components/Modal";
import api from "../service/api";

const FormControl = styled(MuiFormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 200,
}));

const TextField = styled(MuiTextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const ModalButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const validationSchema = Yup.object().shape(
  {
    nroDocumento: Yup.number()
      .typeError("Solo números")
      .when(["apellido", "nombres"], {
        is: (apellido, nombres) => !apellido && !nombres,
        then: Yup.number().typeError("Solo números").required("Al menos un filtro es requerido").min(1000000, "Mínimo 7 dígitos").max(999999999, "Máximo 9 dígitos"),
      }).min(1000000, "Mínimo 7 dígitos").max(999999999, "Máximo 9 dígitos"),
    apellido: Yup.string().when(["nroDocumento", "nombres"], {
      is: (nroDocumento, nombres) => !nroDocumento && !nombres,
      then: Yup.string().required("Al menos un filtro es requerido"),
    })
    .when(["nroDocumento", "nombres"], {
      is: (nroDocumento, nombres) => !nroDocumento && nombres,
      then: Yup.string().required("Debe ingresar apellido"),
    }),    
    nombres: Yup.string().when(["nroDocumento", "apellido"], {
      is: (nroDocumento, apellido) => !nroDocumento && !apellido,
      then: Yup.string().required("Al menos un filtro es requerido"),
    })
    .when(["nroDocumento", "apellido"], {
      is: (nroDocumento, apellido) => !nroDocumento && apellido,
      then: Yup.string().required("Debe ingresar nombres"),
    }),
  },
  [
    ["nroDocumento", "apellido"],
    ["nroDocumento", "nombres"],
    ["apellido", "nombres"],
  ]
);

const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const BuscadorPersonaFisica = (props) => {
  const [datos, setDatos] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [presionoBuscar, setPresionoBuscar] = useState(false);

  const dispatch = useDispatch();
  
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.handleReset();
    setOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTextField = (event) => {    
    if(/^\d*$/.test(event.target.value)){
      formik.setFieldValue(event.target.id, event.target.value);
    }
  };

  const formik = useFormik({
    initialValues: {
      nroDocumento: "",
      apellido: "",
      nombres: "",
      activo: props.activo,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setDatos([]);
      setPage(0);
      setSubmitting(true);
      setPresionoBuscar(true);
      api
        .buscarPersonaFisica(values)
        .then((res) => {
          setDatos(res.data);
        })
        .catch((e) => {
          dispatch(setError(e.response.data.error));
          console.log("Error al cargar los datos");
        })
        .finally(() => setSubmitting(false));
    },
    onReset: () => {
      setDatos([]);
      setPresionoBuscar(false);
    },
  });

  const columns = [
    {
      id: "documento",
      label: "Documento",
      minWidth: 100,
      align: "left",
    },
    {
      id: "apellido",
      label: "Apellido",
      minWidth: 200,
      align: "left",
    },
    {
      id: "nombres",
      label: "Nombres",
      minWidth: 200,
      align: "left",
    },
    {
      id: "domicilio",
      label: "Domicilio",
      minWidth: 200,
      align: "left",
    },
    {
      id: "activo",
      label: "Activo",
      minWidth: 50,
      align: "left",
    },
    {
      id: "acciones",
      label: "Acciones",
      minWidth: 100,
      align: "center",
    },
  ];

  if (props.activo === "S" || props.activo === "N") {
    columns.splice(4, 1);
  }
  return (
    <div>
      {props.mostrar === "boton" ? (
        <ModalButton
          variant="plata"
          onClick={handleOpen}
          endIcon={<SearchIcon />}
        >
          Persona Física
        </ModalButton>
      ) : (
        <IconButton color="primary" disabled={props.disabled} onClick={handleOpen}>
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
      <Modal
        open={open}
        handleClose={handleClose}
        title="BUSCADOR DE PERSONAS FÍSICAS"
        responsiveTitle="PERSONAS FÍSICAS"
        closeButton
        size="md"
      >
        <form
          id="buscador"
          name="buscador"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                id="nroDocumento"
                name="nroDocumento"
                label="Documento"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.nroDocumento}
                onChange={handleChangeTextField}
                error={
                  formik.touched.nroDocumento &&
                  Boolean(formik.errors.nroDocumento)
                }
                helperText={
                  formik.touched.nroDocumento && formik.errors.nroDocumento
                }
              />
            </Grid>
            {props.activo === "S" || props.activo === "N" ? (
              null
            ) : (
              <Grid item xs={12} sm={4}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={formik.touched.activo && Boolean(formik.errors.activo)}
                >
                  <InputLabel id="activoLabel">Activo</InputLabel>
                  <Select
                    labelId="activoLabel"
                    id="activo"
                    name="activo"
                    variant="outlined"
                    label="Activo"
                    value={formik.values.activo}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="T">TODOS</MenuItem>
                    <MenuItem value="S">ACTIVOS</MenuItem>
                    <MenuItem value="N">INACTIVOS</MenuItem>
                  </Select>
                  <FormHelperText>
                    {formik.touched.activo && formik.errors.activo}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                id="apellido"
                name="apellido"
                label="Apellido"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.apellido}
                onChange={formik.handleChange}
                error={
                  formik.touched.apellido && Boolean(formik.errors.apellido)
                }
                helperText={formik.touched.apellido && formik.errors.apellido}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                id="nombres"
                name="nombres"
                label="Nombres"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.nombres}
                onChange={formik.handleChange}
                error={formik.touched.nombres && Boolean(formik.errors.nombres)}
                helperText={formik.touched.nombres && formik.errors.nombres}
              />
            </Grid>

            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              <Button
                variant="plata"
                type="reset"
                endIcon={<CachedIcon />}
                sx={{ m: 1 }}
              >
                {"Limpiar"}
              </Button>{" "}
              <Button
                sx={{ m: 1 }}
                variant="verde"
                color="primary"
                type="submit"
                disabled={submitting}
                endIcon={<SearchIcon />}
              >
                {submitting ? "Buscando..." : "Buscar"}
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <IconButton color="primary" type="reset" title="Limpiar">
                <CachedIcon />
              </IconButton>{" "}
              <IconButton
                color="primary"
                type="submit"
                disabled={submitting}
                title="Buscar"
              >
                <SearchIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12} sm={12}>
              {" "}
            </Grid>
          </Grid>
        </form>

        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small" sx={{ border: "1px solid #dee2e6" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    sx={{
                      backgroundColor: "#fff",
                      color: "#3c4b64",
                      minWidth: column.minWidth,
                    }}
                    key={column.id}
                    align={column.align}
                  >
                    <b>{column.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {datos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === "acciones" ? (
                              <IconButton
                                title="Seleccionar"
                                onClick={() => {
                                  props.resultado(dato);
                                  handleClose();
                                }}
                              >
                                <DoneOutlineRounded sx={{ fontSize: 14 }} />
                              </IconButton>
                            ) : (
                              dato[column.id]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {submitting ? (
                  <TableRow>
                    <TableCell colSpan="7" align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  null
                )}
                {datos.length === 0 && !submitting ? (
                  <TableRow>
                  <TableCell colSpan="7" align="center">
                  {presionoBuscar ? (
                    <Typography variant="aclaracion">
                      No encontramos una persona con los datos ingresados. Si necesitás solicitar el alta en nuestra base de datos podés hacerlo ingresando con el mismo usuario y clave que utilizás para este sistema en <Link href="https://apps.entrerios.gov.ar/vintis/" target="_blank">https://apps.entrerios.gov.ar/vintis/</Link>
                    </Typography>
                  ) : (
                    <Typography variant="aclaracion">No hay datos para mostrar</Typography>
                  )}
                  </TableCell>
                </TableRow>
                ) : (
                  <TableRow>
                  <TableCell colSpan="7" align="center">
                  {presionoBuscar ? (
                    <Typography variant="aclaracion">
                      Si los datos de la persona buscada no son correctos, solicitá su modificación ingresando con el mismo usuario y clave que utilizás para este sistema en <Link href="https://apps.entrerios.gov.ar/vintis/" target="_blank">https://apps.entrerios.gov.ar/vintis/</Link>
                    </Typography>
                  ) : (
                    <Typography variant="aclaracion">No hay datos para mostrar</Typography>
                  )}
                  </TableCell>
                </TableRow>
                )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[
                    5,
                    10,
                    25,
                    { label: "Todas", value: datos.length },
                  ]}
                  colSpan={columns.length}
                  count={datos.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "filas pr página",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Modal>
    </div>
  );
};

export default BuscadorPersonaFisica;
